import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import pick from 'lodash/pick';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import PageTitle from '../../components/PageTitle';
import Toast from '../../components/Toast';
import Container from '../../components/Container';
import Header from '../../components/Header';
import ChangeProfileForm from '../../components/ChangeProfileForm';
import cognitoService from '../../cognito/cognito-service';
import { logoutNonNaviance } from '../Authorization/actions';
import { toGlobalId } from '../../utils/apollo';
import './styles.css';

const messages = defineMessages({
  changeSuccessTitle: {
    id: 'change-profile.success-title',
    defaultMessage: 'Success!',
  },
  changeSuccessDescription: {
    id: 'change-profile.success-description',
    defaultMessage: 'You\'ve updated your account information.',
  },
});

class ChangeProfilePage extends React.PureComponent {
  static propTypes = {
    current: PropTypes.shape({
      viewer: PropTypes.shape({
        idToken: PropTypes.shape({
          email: PropTypes.string,
        }).isRequired,
        userName: PropTypes.string,
      }).isRequired,
    }),
    updateHSAccount: PropTypes.func.isRequired,
    logoutNonNaviance: PropTypes.func.isRequired,
    node: PropTypes.object.isRequired,
    viewer: PropTypes.shape({
      enabledFeatures: PropTypes.array,
      uuid: PropTypes.string,
    }),
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }),
  };

  state = {
    success: false,
    requesting: false,
    updateError: {},
  };

  getUser = () => {
    const { node, current } = this.props;
    const { viewer } = current;
    if (node && node.userList && viewer.userName) {
      const userId = toGlobalId('HSUser', viewer.userName);
      return node.userList.find((user) => user.id === userId || user.email === viewer.idToken.email);
    }
    return null;
  };

  hideSuccessMessage = () => {
    this.setState({ success: false });
  };

  _handleSave = (updatedUser) => {
    this.setState({
      requesting: true,
      updateError: {},
    });

    const requests = [];
    if (updatedUser.password) {
      requests.push(this._updatePassword(updatedUser));
    }

    const userProperties = pick(updatedUser, ['firstName', 'lastName', 'email']);
    if (Object.keys(userProperties).length > 0) {
      requests.push(this._updateAttributes(userProperties));
    }
    Promise.all(requests).then((results) => {
      const newState = {
        requesting: false,
      };
      newState.updateError = results.reduce((accumulator, result) => Object.assign(accumulator, result), {});
      if (!Object.keys(newState.updateError).length) {
        newState.success = true;
      }
      this.setState(newState);
    });
  };

  _updateAttributes(userProperties) {
    return new Promise((resolve) => {
      this.props.updateHSAccount({
        variables: {
          input: {
            hsAccount: {
              hsUser: { ...userProperties, id: this.getUser().id },
              id: this.props.node.id,
            },
          },
        },
      })
        .then(() => {
          resolve();
        }, (error) => {
          if (error && error.message.includes('already exists')) {
            resolve({ userExists: true });
          } else {
            resolve({ generalError: true });
          }
        });
    });
  }

  _updatePassword(updatedUser) {
    return cognitoService.getCurrentUserWithSession()
      .then((currentUser) => new Promise((resolve) => {
        currentUser.changePassword(updatedUser.password, updatedUser.newPassword, (err, result) => {
          if (err) {
            if (err.message.includes('previousPassword')) {
              resolve({ previousPasswordFailure: true });
            } else {
              resolve({ generalError: true });
            }
          } else {
            resolve();
          }
        });
      }), (err) => this.props.logoutNonNaviance(this.props.current.viewer));
  }

  render() {
    const { updateError, requesting, success } = this.state;
    const { viewer, history, current } = this.props;
    const isCommunityServerlessOn = viewer?.enabledFeatures?.includes('COMMUNITYSERVERLESS');
    const user = this.getUser();
    if (!user) {
      return (
        <p>
          <PageTitle title="User" category="Settings" />
          <FormattedMessage id="user.update.notFound" defaultMessage="No User Found" />
        </p>
      );
    }
    return (
      <div>
        <Container text>
          <Header as="h2" styleName="settings-header">
            <FormattedMessage id="settings.accountinfo.header" defaultMessage="Account Information" />
          </Header>
          <Toast
            active={success}
            title={<FormattedMessage {...messages.changeSuccessTitle} />}
            description={<FormattedMessage {...messages.changeSuccessDescription} />}
            onDeactivate={this.hideSuccessMessage}
          />
          <ChangeProfileForm
            requesting={requesting}
            user={user}
            onSave={this._handleSave}
            updateError={updateError}
            uuid={viewer?.uuid}
            isCommunityServerlessOn={isCommunityServerlessOn}
            history={history}
            current={current}
          />
        </Container>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  current: state.current,
});

export const ChangeProfilePageComponent = injectIntl(ChangeProfilePage);
export default connect(mapStateToProps, { logoutNonNaviance })(ChangeProfilePageComponent);
